import React, { useState, useEffect } from "react";
import { Radio, notification } from "antd";
import type { RadioChangeEvent } from "antd";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { subDays } from "date-fns";

import { SendCompleteIcon, ConfirmFailIcon } from "icons";
import { Button, Input, DateRangePicker, ScrollToTop } from "components";
import { localStorageHelper, handleConvertMonthYear } from "utils";
import ConfirmModal from "screens/changeStudentAffiliation/ConfirmModal";
import SecondConfirmModal from "screens/changeStudentAffiliation/SecondConfirmModal";

import { Details } from "models/stressCheck/stressCheckDetails";
import { UpdateStressCheckData } from "models/stressCheck/updateStressCheck";
import {
  UpdateStressCheckActionCreators,
  DeleteStressCheckActionCreators,
  StressCheckDetailsActionCreators,
} from "redux/rootActions";
import {
  selectIsDeletingStressCheck,
  selectIsDeleted,
  selectDeleteErrorMessage,
} from "redux/stressCheck/deleteStressCheck/deleteStressCheckState";
import {
  selectIsUpdated,
  selectUpdateErrorMessage,
} from "redux/stressCheck/updateStressCheck/updateStressCheckStates";

import {
  AudioContainer,
  ItemsContainer,
  LeftItem,
  RightItem,
  TextTitle,
  AudioWrapper,
  TitleContainer,
  DateRangeItem,
  SingleButtonWrapper,
  ItemsContainerNumber,
} from "screens/mentalHealth/MentalHealth.style";

interface IStressCheckEdit {
  detailsData: Details;
  handleCancel: () => void;
  setEdit: (edit: boolean) => void;
}

export const StressCheckEdit: React.FC<IStressCheckEdit> = ({
  detailsData,
  handleCancel,
  setEdit,
}) => {
  const { t }: any = useTranslation("translation", {
    keyPrefix: "mental-health",
  });
  const { t: t1 }: any = useTranslation("translation", {
    keyPrefix: "stress-check",
  });
  const { t: t2 }: any = useTranslation("translation", {
    keyPrefix: "message-delivery",
  });
  const currentLanguage = localStorageHelper.getItem("i18nextLng");
  const dispatch = useDispatch();
  const stressCheckUpdated = useSelector(selectIsUpdated);
  const stressCheckDeleted = useSelector(selectIsDeleted);
  const isDeletingStressCheck = useSelector(selectIsDeletingStressCheck);
  const deleteStressCheckError = useSelector(selectDeleteErrorMessage);
  const updateStressCheckError = useSelector(selectUpdateErrorMessage);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(moment().add(1, "M").toDate());
  const [examinationPassed, setExaminationPassed] = useState<boolean>(false);
  const [isVisibleModal, setIsVisibleModal] = useState<boolean>(false);
  const [isVisibleDeleteModal, setIsVisibleDeleteModal] =
    useState<boolean>(false);
  const [testName, setTestName] = useState<string>("");
  const [radioValue, setRadioValue] = useState<number>(1);
  const [error, setError] = useState({
    status: "",
    error: "",
  });

  const onChangeRadio = (e: RadioChangeEvent) => {
    setRadioValue(e.target.value);
  };

  const onChangeTestName = (e: any) => {
    setTestName(e.target.value);
    if (error.status === "test-name") {
      setError({
        status: "",
        error: "",
      });
    }
  };

  const onUpdateTest = () => {
    if (!testName) {
      setError({
        status: "test-name",
        error: "enter-test-name",
      });
      return;
    }
    if (examinationPassed) {
      setError({
        status: "passed",
        error: "exam-period-passed",
      });
      return;
    }
    const updateStressCheckParams: UpdateStressCheckData = {
      id: detailsData.id,
      stress_check_type_id: radioValue,
      name: testName,
      start_time: moment(startDate).toString(),
      end_time: moment(endDate).toString(),
      temp_save_to_permant_save: "true",
    };
    dispatch(
      UpdateStressCheckActionCreators.updateStressCheckAction(
        updateStressCheckParams
      )
    );
  };

  const deleteStressCheck = () => {
    dispatch(
      DeleteStressCheckActionCreators.deleteStressCheckAction({
        id: detailsData.id,
      })
    );
    setIsVisibleDeleteModal(false);
  };

  useEffect(() => {
    if (moment(endDate).diff(moment(subDays(new Date(), 1))) < 0) {
      setExaminationPassed(true);
    } else {
      setExaminationPassed(false);
      setError({
        status: "",
        error: "",
      });
    }
  }, [endDate]);

  useEffect(() => {
    if (stressCheckUpdated) {
      notification.success({
        placement: "bottomRight",
        message: t("saved"),
        icon: <SendCompleteIcon />,
      });
      dispatch(UpdateStressCheckActionCreators.handleResetDataAction());
      handleCancel();
    }
  }, [stressCheckUpdated]);

  useEffect(() => {
    if (stressCheckDeleted) {
      notification.success({
        placement: "bottomRight",
        message: t("deleted"),
        icon: <SendCompleteIcon />,
      });
      dispatch(DeleteStressCheckActionCreators.handleResetDataAction());
      handleCancel();
    }
  }, [stressCheckDeleted]);

  useEffect(() => {
    if (deleteStressCheckError) {
      notification.error({
        placement: "bottomRight",
        message: deleteStressCheckError,
        icon: <ConfirmFailIcon width={53} height={53} />,
      });
      dispatch(DeleteStressCheckActionCreators.handleResetDataAction());
    }
  }, [deleteStressCheckError]);

  useEffect(() => {
    if (updateStressCheckError) {
      notification.error({
        placement: "bottomRight",
        message: updateStressCheckError,
        icon: <ConfirmFailIcon width={53} height={53} />,
      });
      dispatch(UpdateStressCheckActionCreators.handleResetDataAction());
    }
  }, [updateStressCheckError]);

  useEffect(() => {
    if (detailsData) {
      const examinationPeriod: string[] =
        detailsData.examination_period.split("〜");
      setRadioValue(detailsData.stress_check_type_id);
      setTestName(detailsData.name);
      if (examinationPeriod.length) {
        setStartDate(moment(examinationPeriod[0]).toDate());
        setEndDate(moment(examinationPeriod[1]).toDate());
      }
    }
  }, [detailsData]);

  useEffect(() => {
    dispatch(DeleteStressCheckActionCreators.handleResetDataAction());
    dispatch(UpdateStressCheckActionCreators.handleResetDataAction());
    setExaminationPassed(false);
    setIsVisibleModal(false);
    setError({
      status: "",
      error: "",
    });
  }, []);

  return (
    <>
      <ScrollToTop />
      <TitleContainer
        style={{
          paddingBottom: 12,
          marginBottom: 0,
          borderBottom: "1px solid #F2F2F2",
        }}
      >
        <TextTitle>{t1("stress-check-edit")}</TextTitle>
        <Button
          color="#FFFFFF"
          fontSize={16}
          fontWeight={700}
          lineHeight="16px"
          background={"#3B3B3B"}
          padding="11px 24px 13px"
          name={t("stop-editing")}
          bdr="8px"
          border="none"
          onClick={() => setIsVisibleModal(true)}
        />
      </TitleContainer>
      <ItemsContainerNumber
        style={{
          paddingTop: 22,
          paddingBottom: 22,
        }}
      >
        <LeftItem>{t("action-item-number")}</LeftItem>
        <Radio.Group onChange={onChangeRadio} value={radioValue}>
          <AudioWrapper>
            <Radio value={1}>{t1("25-questions")}</Radio>
            <Radio value={2}>{t1("59-questions")}</Radio>
          </AudioWrapper>
        </Radio.Group>
      </ItemsContainerNumber>
      <AudioContainer>
        <LeftItem>{t("test-name")}</LeftItem>
        <RightItem>
          <Input
            height={40}
            value={testName}
            onChange={onChangeTestName}
            placeholder={`${handleConvertMonthYear(
              moment(startDate).format("YYYY-MM-DD"),
              currentLanguage
            )}${t1("stress-check-placeholder")}`}
          />
          {error.status === "test-name" && error.error && (
            <div className="red-text">{t(error.error)}</div>
          )}
        </RightItem>
      </AudioContainer>
      <ItemsContainer>
        <LeftItem>{t("examination-period")}</LeftItem>
        <DateRangeItem>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            locale={currentLanguage}
            maxDate={false}
          />
          {error.status === "passed" && error.error && (
            <div className="red-text">{t(error.error)}</div>
          )}
        </DateRangeItem>
      </ItemsContainer>

      <SingleButtonWrapper>
        <Button
          color="#FFFFFF"
          fontSize={16}
          fontWeight={700}
          lineHeight="16px"
          background={"#2AC769"}
          padding="11px 40px 13px"
          name={t("save")}
          bdr="6px"
          border="none"
          onClick={onUpdateTest}
        />
        <div
          onClick={() => setIsVisibleDeleteModal(true)}
          style={{
            width: "max-content",
            cursor: isDeletingStressCheck ? "not-allowed" : "pointer",
          }}
        >
          {t("delete-action")}
        </div>
      </SingleButtonWrapper>
      {isVisibleModal && (
        <ConfirmModal
          isVisible={isVisibleModal}
          handleCancel={() => setIsVisibleModal(false)}
          handleOk={() => {
            setEdit(false);
            dispatch(
              StressCheckDetailsActionCreators.stressCheckDetailsAction({
                id: detailsData.id,
                language_code: currentLanguage,
              })
            );
          }}
        >
          <div
            style={{
              fontWeight: 500,
              fontSize: 14,
              lineHeight: "20px",
            }}
          >
            {t("confirm-leaving")}
          </div>
        </ConfirmModal>
      )}
      {isVisibleDeleteModal && (
        <SecondConfirmModal
          type="delete"
          isVisible={isVisibleDeleteModal}
          handleCancel={() => setIsVisibleDeleteModal(false)}
          handleOk={deleteStressCheck}
        >
          <div
            style={{
              fontWeight: 500,
              fontSize: 14,
              lineHeight: "20px",
            }}
          >
            {t("confirm-delete")}
            {t2("are-you-ok")}
          </div>
        </SecondConfirmModal>
      )}
    </>
  );
};

export default StressCheckEdit;
